import React, { useState, useEffect } from 'react';
import { Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { MdEdit } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import SFCustomTable from '../../component/custom/SFCustomTable';
import ApiRequestService from '../../component/ApiRequestService';
import { NotificationManager } from 'react-notifications';
import DeleteDialog from '../DeleteDialog';
import { browserHistory } from 'react-router';
import { AppProperties } from '../../common/function';
import CustomToggle from '../../component/CustomToggle';
import { useLibrarySync, UnsyncedCategoriesDialog, ReadOnlyActionsCell, SyncedAtSourceCell, SyncLoadingCell, refuseEmptyRowSync, hasValidLibraryIds } from './LibrarySync';
import "./style.css";

const AssignmentMatrixLibrarySubject = () => {

    const appProperties = AppProperties();

    const [librarySubjectID, setLibrarySubjectID] = useState(null);
    const [librarySubjectName, setLibrarySubjectName] = useState("");
    const [librarySubject, setLibrarySubject] = useState(null);
    const [libraryTopics, setLibraryTopics] = useState([]);

    // True when this subject arrived from another university, which also puts
    // adding a topic to it out of reach. The subject answers this even when it
    // has no topics yet; the topics answer it if the subject read failed.
    const isReadOnly = !!librarySubject?.is_synced || libraryTopics.some(topic => topic.is_synced);

    const [showAddTopicDialog, setShowAddTopicDialog] = useState(false);
    const [editTopic, setEditTopic] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [showTopicDeleteDialog, setShowTopicDeleteDialog] = useState(false);

    const {
        syncAccess,
        syncLoading,
        syncStatusLoading,
        loadSyncStatus,
        reloadSyncStatus,
        getSyncInfo,
        toggleSync,
        unsyncedCategories,
        clearUnsyncedCategories,
    } = useLibrarySync({
        statusUrl: '/librarySyncTopicsStatus',
        toggleUrl: '/librarySyncEnableTopic',
    });

    const onEnableTopicSync = (row) => {
        if (refuseEmptyRowSync('topic', row)) return;
        toggleSync(row.id, true, {
            aml_subject_id: librarySubjectID,
            aml_topic_id: row.id,
        });
    }

    const onDisableTopicSync = (row) => {
        toggleSync(row.id, false, {
            aml_subject_id: librarySubjectID,
            aml_topic_id: row.id,
        });
    }

    // Dropped entirely without sync access - see AssignmentMatrixLibrary.js.
    const topicColumns = [
        {
            name: <div className='sf-data-table-header'>ID</div>,
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: <div className='sf-data-table-header'>Topic Name</div>,
            selector: row => row.name,
            sortable: true,
            grow: 2,
        },
        {
            name: <div className='sf-data-table-header'># of Matrix</div>,
            selector: row => row.total_matrix_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'># of Assignments</div>,
            selector: row => row.total_quiz_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'>Created By</div>,
            selector: row => row.created_by,
            sortable: true,
            grow: 2
        },
        {
            id: 'sync',
            name: <div className='sf-data-table-header'>Sync</div>,
            sortable: false,
            width: '180px',
            cell: (row) => {
                let syncInfo = getSyncInfo(row.id);
                // Topics of a subject owned elsewhere are read-only here.
                if (row.is_synced || syncInfo.is_synced) {
                    return <SyncedAtSourceCell
                        amlSubjectID={librarySubjectID}
                        sourcePath={`AssignmentMatrixLibraryTopic?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${row.id}&topic_name=${encodeURIComponent(row.name)}`}
                    />;
                }
                if (syncStatusLoading) {
                    return <SyncLoadingCell />;
                }
                return (
                    <div style={{ marginTop: "8px" }}>
                        <CustomToggle
                            Enabled={syncInfo.enable_sync || false}
                            tristate={syncInfo.some_sync ? 1 : false}
                            onEnableChecked={() => onEnableTopicSync(row)}
                            onDisableChecked={() => onDisableTopicSync(row)}
                            onHandleMiddleredirect={() => {
                                let baseFolder = appProperties.baseFolder;
                                let url = `${baseFolder}AssignmentMatrixLibraryTopic?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${row.id}&topic_name=${encodeURIComponent(row.name)}`;
                                browserHistory.push(url);
                            }}
                            syncStatus={syncInfo.sync_status_code || 0}
                            sourceRedirect={false}
                            type="switch"
                            id={"switch-libTopic-" + row.id}
                        />
                    </div>
                );
            },
        },
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            cell: (row, rowIndex) => {
                if (row.is_synced) {
                    return <ReadOnlyActionsCell />;
                }
                return (
                    <div>
                        <IconButton onClick={() => handleEditTopicClick(row)}>
                            <MdEdit color='#3d5e9c' />
                        </IconButton>
                        <IconButton onClick={() => {
                            setShowTopicDeleteDialog(true);
                            setSelectedTopic(row);
                        }}>
                            <FiTrash2 color='#bb0000' />
                        </IconButton>
                    </div>
                )
            },
        },
    ].filter(column => syncAccess || column.id !== 'sync');

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        let idStr = queryParams.get("id");
        let nameStr = queryParams.get("name");
        if (nameStr) {
            setLibrarySubjectName(decodeURIComponent(nameStr));
        }
        if (idStr) {
            let id = parseInt(idStr, 10);
            if (id) {
                setLibrarySubjectID(id);
                getLibrarySubject(id);
                getLibraryTopics(id);
                loadSyncStatus({ aml_subject_id: id });
            }
        }
    }, []);

    const getLibrarySubject = (subjectID) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLSubject',
            data: {
                id: subjectID,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setLibrarySubject(res.data.data);
                if (!librarySubjectName && res.data.data.name) {
                    setLibrarySubjectName(res.data.data.name);
                }
            } else {
                setLibrarySubject(null);
            }
        })
    }

    const getLibraryTopics = (subjectID) => {
        ApiRequestService({
            method: 'post',
            url: '/getAMLTopics',
            data: {
                aml_subject_id: subjectID,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setLibraryTopics(res.data.data);
            } else {
                setLibraryTopics([]);
            }
        })
    }

    const handleAddTopicClick = () => {
        setEditTopic(null);
        setShowAddTopicDialog(true);
    }

    const handleEditTopicClick = (topic) => {
        setEditTopic(topic);
        setShowAddTopicDialog(true);
    }

    const onAddLibraryTopic = (newData) => {
        setLibraryTopics([...libraryTopics, newData]);
        setShowAddTopicDialog(false);
        reloadSyncStatus();
    }

    const onEditLibraryTopic = (newData) => {
        let index = libraryTopics.findIndex(t => t.id === newData.id);
        if (index > -1) {
            let updated = [...libraryTopics];
            updated[index] = newData;
            setLibraryTopics(updated);
        }
        setEditTopic(null);
        setShowAddTopicDialog(false);
        reloadSyncStatus();
    }

    const handleDeleteTopic = () => {
        if (!hasValidLibraryIds(librarySubjectID, selectedTopic?.id)) {
            NotificationManager.error("Could not delete the topic: missing subject or topic id", "ERROR");
            return;
        }
        ApiRequestService({
            method: 'post',
            url: '/deleteAMLTopic',
            data: {
                // Topic ids are allocated per subject, so the subject is half
                // of the key. Without it the server matched nothing and still
                // answered success.
                aml_subject_id: librarySubjectID,
                id: selectedTopic.id,
            }
        }).then(res => {
            // Reads the status, not the fact that the call came back - see
            // handleDeleteSubject in AssignmentMatrixLibrary.js.
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not delete the topic", "ERROR");
                return;
            }

            setLibraryTopics(current => current.filter(t => t.id !== selectedTopic.id));
            setShowTopicDeleteDialog(false);
            setSelectedTopic(null);
            reloadSyncStatus();
            NotificationManager.success("Successfully deleted the topic");
        }).catch(err => {
            console.error("Error deleting library topic:", err);
            NotificationManager.error("Could not delete the topic", "ERROR");
        })
    }

    return (
        <div className='assign-lib-cont'>
            <div className='assign-lib-breadcrumb'>
                <span
                    className='assign-lib-breadcrumb-link'
                    onClick={() => {
                        let baseFolder = appProperties.baseFolder;
                        browserHistory.push(`${baseFolder}AssignmentMatrixLibrary`);
                    }}
                >
                    Assignment Matrix Library
                </span>
                <span className='assign-lib-breadcrumb-separator'>/</span>
                <span className='assign-lib-breadcrumb-current'>
                    {librarySubjectName || "Topics"}
                </span>
            </div>
            <Typography fontWeight={500} fontSize={24}>
                {librarySubject ? `${librarySubject.name} -` : null} Topics
            </Typography>
            <div style={{ marginTop: '1rem' }}>
                <SFCustomTable
                    loading={syncLoading}
                    selectableRows={false}
                    columns={topicColumns}
                    data={libraryTopics}
                    rightContent={
                        <div>
                            {isReadOnly ? null :
                                <Button variant='contained' onClick={handleAddTopicClick}>Add Topic</Button>}
                        </div>
                    }
                    topPagination={false}
                    bottomPagination={true}
                    handleRowClick={(topic) => {
                        let baseFolder = appProperties.baseFolder;
                        let url = `${baseFolder}AssignmentMatrixLibraryTopic?subject_id=${librarySubjectID}&subject_name=${encodeURIComponent(librarySubjectName)}&topic_id=${topic.id}&topic_name=${encodeURIComponent(topic.name)}`;
                        browserHistory.push(url);
                    }}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchPlaceholder={"Search by topic name"}
                    searchByFields={["name"]}
                />
            </div>

            <AddTopicDialog
                open={showAddTopicDialog}
                editTopic={editTopic}
                subjectID={librarySubjectID}
                handleClose={() => {
                    setShowAddTopicDialog(false);
                    setEditTopic(null);
                }}
                onAddLibraryTopic={onAddLibraryTopic}
                onEditLibraryTopic={onEditLibraryTopic}
            />

            <UnsyncedCategoriesDialog
                unsyncedCategories={unsyncedCategories}
                onClose={clearUnsyncedCategories}
            />

            <DeleteDialog
                showDialog={showTopicDeleteDialog}
                title="Delete Topic"
                description={
                    <div>
                        <div>Are you sure you want to delete "{selectedTopic?.name}" topic?</div>
                        {selectedTopic?.total_quiz_count > 0 ?
                            <div>
                                <br />
                                <strong>WARNING!</strong>: Deleting this topic will also delete all assignments linked to it.
                            </div>
                            : null}
                    </div>
                }
                onCancel={() => {
                    setShowTopicDeleteDialog(false);
                }}
                onDelete={() => {
                    handleDeleteTopic();
                }}
            />
        </div>
    );
}

const AddTopicDialog = ({ open, handleClose, editTopic, subjectID, onAddLibraryTopic, onEditLibraryTopic }) => {
    const [name, setName] = useState("");

    useEffect(() => {
        if (editTopic) {
            setName(editTopic.name);
        } else {
            setName("");
        }
    }, [editTopic]);

    const addLibraryTopic = () => {
        ApiRequestService({
            method: 'post',
            url: '/addAMLTopic',
            data: {
                name: name,
                aml_subject_id: subjectID,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                onAddLibraryTopic(res.data.data);
            }
        })
    }

    const editLibraryTopic = () => {
        let payload = {
            ...editTopic,
            name: name,
        }
        ApiRequestService({
            method: 'post',
            url: '/editAMLTopic',
            data: payload,
        }).then(res => {
            if (res && res.data && res.data.data) {
                onEditLibraryTopic(payload);
            }
        })
    }

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Typography fontSize={18} fontWeight={500}>
                    {editTopic ? "Edit Topic" : "New Topic"}
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                }}
                onClick={handleClose}
            >
                <IoMdCloseCircleOutline fontSize={24} />
            </IconButton>
            <DialogContent>
                <TextField
                    placeholder='Enter topic name'
                    sx={{ width: "100%" }}
                    value={name}
                    onChange={event => setName(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleClose}>
                    Close
                </Button>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (editTopic) {
                            editLibraryTopic();
                        } else {
                            addLibraryTopic();
                        }
                    }}
                    disabled={name === ""}
                >
                    {editTopic ? "Update" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AssignmentMatrixLibrarySubject;
